import { useEffect, useMemo, useState } from "react";
import { BRANCHES, branchByNumber } from "../data/branches";
import { sameBranchNumbers, toggleBranchNumber } from "../lib/traffyBranchGroups";

type BranchGroup = { id: string; name: string; branches: string[]; createdAt: string; updatedAt: string };

async function apiJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, { credentials: "same-origin", cache: "no-store", ...init });
  const body = await response.json().catch(() => ({})) as T & { error?: { message?: string } };
  if (!response.ok) throw new Error(body.error?.message || `API ${response.status}`);
  return body;
}

function label(number: string): string {
  const branch = branchByNumber(number);
  return branch ? `${number} ${branch.shortName}` : number;
}

export default function BranchGroupFilter({ selectedBranches, onChange, availableBranches }: {
  selectedBranches: string[];
  onChange: (branches: string[]) => void;
  availableBranches?: string[];
}) {
  const branches = useMemo(() => availableBranches?.length ? [...new Set(availableBranches)].sort() : BRANCHES.map((branch) => branch.number), [availableBranches]);
  const [groups, setGroups] = useState<BranchGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [editor, setEditor] = useState<"new" | "edit" | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const selectedGroup = groups.find((group) => group.id === selectedGroupId) ?? null;
  const dirty = Boolean(selectedGroup && !sameBranchNumbers(selectedGroup.branches, selectedBranches));

  const load = async () => setGroups((await apiJson<{ groups: BranchGroup[] }>("/api/branch-groups")).groups);
  useEffect(() => { void load().catch((reason) => setMessage(reason instanceof Error ? reason.message : "โหลดกลุ่มสาขาไม่สำเร็จ")); }, []);

  const applyGroup = (id: string) => {
    setSelectedGroupId(id); setEditor(null); setMessage("");
    const group = groups.find((item) => item.id === id);
    if (group) onChange([...group.branches]);
  };
  const save = async () => {
    if (!name.trim() || !selectedBranches.length || busy) return;
    setBusy(true); setMessage("");
    try {
      const editing = editor === "edit" && selectedGroup;
      const result = await apiJson<{ group: BranchGroup }>(editing ? `/api/branch-groups/${selectedGroup.id}` : "/api/branch-groups", {
        method: editing ? "PUT" : "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, branches: selectedBranches }),
      });
      await load(); setSelectedGroupId(result.group.id); setEditor(null); setMessage(editing ? "อัปเดตกลุ่มแล้ว" : "บันทึกกลุ่มแล้ว");
    } catch (reason) { setMessage(reason instanceof Error ? reason.message : "บันทึกกลุ่มไม่สำเร็จ"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!selectedGroup || busy || !window.confirm(`ลบกลุ่ม “${selectedGroup.name}” หรือไม่?`)) return;
    setBusy(true); setMessage("");
    try {
      await apiJson(`/api/branch-groups/${selectedGroup.id}`, { method: "DELETE" });
      setGroups((current) => current.filter((group) => group.id !== selectedGroup.id)); setSelectedGroupId(""); setEditor(null); setMessage("ลบกลุ่มแล้ว");
    } catch (reason) { setMessage(reason instanceof Error ? reason.message : "ลบกลุ่มไม่สำเร็จ"); }
    finally { setBusy(false); }
  };

  return <div className="branch-group-filter">
    <label><span>กลุ่มสาขา</span><select value={selectedGroupId} onChange={(event) => applyGroup(event.target.value)}><option value="">เลือกกลุ่มส่วนตัว</option>{groups.map((group) => <option key={group.id} value={group.id}>{group.name} ({group.branches.length})</option>)}</select></label>
    <div className="branch-group-picker"><span>สาขา (เลือกได้หลายสาขา)</span><details><summary>{selectedBranches.length ? `เลือกแล้ว ${selectedBranches.length} สาขา` : "ทุกสาขา"}</summary><div className="branch-group-menu"><div><button type="button" onClick={() => onChange(branches)}>เลือกทั้งหมด</button><button type="button" onClick={() => onChange([])}>ล้างทั้งหมด</button></div>{branches.map((branch) => <label key={branch}><input type="checkbox" checked={selectedBranches.includes(branch)} onChange={() => onChange(toggleBranchNumber(selectedBranches, branch))}/><span>{label(branch)}</span></label>)}</div></details></div>
    <div className="branch-group-actions"><button type="button" disabled={!selectedBranches.length || busy} onClick={() => { setEditor("new"); setName(""); setMessage(""); }}>บันทึกกลุ่มใหม่</button>{selectedGroup && <><button type="button" disabled={!selectedBranches.length || busy} onClick={() => { setEditor("edit"); setName(selectedGroup.name); setMessage(""); }}>{dirty ? "อัปเดตกลุ่ม" : "แก้ไขชื่อกลุ่ม"}</button><button type="button" className="danger" disabled={busy} onClick={() => void remove()}>ลบ</button></>}</div>
    {editor && <div className="branch-group-editor"><label><span>ชื่อกลุ่ม</span><input autoFocus maxLength={80} value={name} onChange={(event) => setName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); void save(); } }} placeholder="เช่น กลุ่มเฝ้าระวัง"/></label><button type="button" disabled={!name.trim() || !selectedBranches.length || busy} onClick={() => void save()}>{busy ? "กำลังบันทึก..." : "บันทึก"}</button><button type="button" onClick={() => setEditor(null)}>ยกเลิก</button></div>}
    {selectedGroup && <small>{selectedGroup.name}: {selectedGroup.branches.map(label).join(", ")}{dirty ? " · รายการเปลี่ยนแล้ว" : ""}</small>}
    {message && <small className="branch-group-message" role="status">{message}</small>}
  </div>;
}
